import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";
import CommonModal from "../components/CommonModal";

const Commodity = () => {
  const formFields = useMemo(() => [
    { name: "full_name", label: "Full Name", type: "text", required: true, col: 6 },
    { name: "short_name", label: "Short Name", type: "text", col: 6 },
    { name: "address", label: "Address", type: "text", col: 6 },
    {
      name: "status",
      label: "Status",
      type: "select",
      options: [
        { value: 0, label: "Active" },
        { value: 1, label: "Inactive" },
      ],
      col: 6,
    },
  ], []);

  const initialFormState = useMemo(() => {
    return formFields.reduce((acc, field) => {
      acc[field.name] = "";
      return acc;
    }, { commodity_id: 0 });
  }, [formFields]);

  const [formData, setFormData]       = useState(initialFormState);
  const [commodityList, setCommodityList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showModal, setShowModal]     = useState(false);
  const [photo, setPhoto]               = useState(null);   // new File object
  const [existingPhoto, setExistingPhoto] = useState("");   // URL from server
  const [removePhoto, setRemovePhoto]   = useState(false);  // flag to delete existing
  const fileInputRef = useRef(null);
  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({ icon, title, text: text || "Something went wrong.", timer, showConfirmButton: false });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const response = await post("/masters/commodity/list");
      if (response.status === 200) setCommodityList(response.data);
      else showAlert("error", response.status, response.message);
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  }, [showAlert]);

  useEffect(() => { fetchData(); }, [fetchData]);
  const handleEdit = useCallback((row) => {
    setFormData({ ...initialFormState, ...row });
    setExistingPhoto(row.photo || "");
    setPhoto(null);
    setRemovePhoto(false);
    setShowModal(true);
  }, [initialFormState]);
  const columns = useMemo(() => [
    { header: "S.No", label_condition: "s_no", enableColumnFilter: false, enableSorting: false },
    {
      header: "Actions",
      cell: ({ row }) => (
        <i
          className="ri-file-edit-fill text-primary cursor-pointer me-2"
          onClick={() => handleEdit(row)}
          title="Edit"
          style={{ fontSize: "18px" }}
        />
      ),
      enableSorting: false,
      enableColumnFilter: false,
    },
    { header: "Full Name", accessorKey: "full_name" },
    { header: "Short Name", accessorKey: "short_name" },
    { header: "Status", accessorKey: "status", label_condition: "status" },
    {
      header: "Photo",
      cell: ({ row }) =>
        row.photo ? (
          <img
            src={row.photo}
            alt="commodity"
            style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 6, border: "1px solid #dee2e6" }}
          />
        ) : (
          <span className="text-muted">—</span>
        ),
      enableSorting: false,
      enableColumnFilter: false,
    },
  ], [handleEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!validTypes.includes(file.type)) {
      showAlert("warning", "Invalid File", "Only JPEG, PNG, WEBP, or GIF allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showAlert("warning", "File Too Large", "Photo must be under 5MB.");
      return;
    }

    setPhoto(file);
    setRemovePhoto(false);
    e.target.value = "";
  };

  const handleRemovePhoto = () => {
    if (existingPhoto) setRemovePhoto(true);
    setPhoto(null);
    setExistingPhoto("");
  };

  const previewSrc = photo ? URL.createObjectURL(photo) : existingPhoto || null;

  const handleClose = () => {
    setShowModal(false);
    setFormData(initialFormState);
    setPhoto(null);
    setExistingPhoto("");
    setRemovePhoto(false);
  };

  const handleSubmit = async () => {
    for (let field of formFields.filter((f) => f.required)) {
      if (!formData[field.name]) {
        showAlert("error", "Oops...", `${field.label} is required`);
        return;
      }
    }

    try {
      const payload = new FormData();

      Object.entries(formData).forEach(([key, value]) => payload.append(key, value));

      if (photo) payload.append("photo", photo);
      if (removePhoto) payload.append("remove_photo", "true");

      const response = await post("/masters/commodity", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        showAlert("success", "Saved", response.message);
        fetchData();
        handleClose();
      } else {
        showAlert("error", response.status, response.message);
      }
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  };

  const PhotoUploadSection = (
    <div className="mt-3">
      <label className="form-label fw-semibold">Commodity Photo</label>

      {previewSrc ? (
        <div className="d-flex align-items-center gap-3">
          <div className="position-relative" style={{ width: 100, height: 100 }}>
            <img
              src={previewSrc}
              alt="preview"
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8, border: "1px solid #dee2e6" }}
            />
            <button
              type="button"
              className="btn btn-danger btn-sm position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center"
              style={{ width: 22, height: 22, fontSize: 12, borderRadius: "50%", transform: "translate(40%, -40%)" }}
              onClick={handleRemovePhoto}
              title="Remove photo"
            >
              <i className="ri-close-line" />
            </button>
          </div>
          <div>
            <p className="mb-1 small text-muted">
              {photo ? photo.name : "Current photo"}
            </p>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => fileInputRef.current?.click()}
            >
              <i className="ri-refresh-line me-1" />
              Change Photo
            </button>
          </div>
        </div>
      ) : (
        <div
          className="border rounded p-4 text-center"
          style={{ borderStyle: "dashed", borderColor: "#6c63ff", cursor: "pointer", background: "#f8f7ff" }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) handlePhotoSelect({ target: { files: [file], value: "" } });
          }}
        >
          <i className="ri-image-add-line" style={{ fontSize: 32, color: "#6c63ff" }} />
          <p className="mb-0 text-muted small mt-2">
            Click or drag & drop a photo here
            <br />
            <span style={{ fontSize: 11 }}>JPEG, PNG, WEBP · max 5MB</span>
          </p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        style={{ display: "none" }}
        onChange={handlePhotoSelect}
      />
    </div>
  );

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">Commodity</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
          Add Commodity
        </button>
      </div>

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Commodity List</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Commodity List"
            columns={columns}
            data={commodityList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>

      <CommonModal
        show={showModal}
        title="Commodity"
        formFields={formFields}
        formData={formData}
        onChange={handleChange}
        onClose={handleClose}
        onSubmit={handleSubmit}
        submitText="Save"
        extraContent={PhotoUploadSection}
      />
    </>
  );
};

export default Commodity;